import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    pathPattern: String, // Regex pattern to match paths where this should be active (e.g., "/labs/.*/edit")
    excludeSelectors: Array // CSS selectors for links that should be excluded from the check
  }

  static targets = ['form']

  connect() {
    this.isSubmitting = false
    this.initializeUnsavedChangesDetection()
    this.storeInitialValues()
  }

  initializeUnsavedChangesDetection() {
    if (!this.isOnTargetPath()) return

    this.defaultExcludeSelectors = [
      '.document-link',
      '.label-link', 
      '.btn-add-shipment',
      '.btn-add-line-item',
      '.btn-add-document'
    ]

    // Listen for form submissions to reset dirty state
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
    if (form) form.addEventListener('submit', this.handleFormSubmit.bind(this))

    document.addEventListener('click', this.handleNavigationAttempt.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleNavigationAttempt.bind(this))
    
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
    if (form) form.removeEventListener('submit', this.handleFormSubmit.bind(this))
  }

  handleNavigationAttempt(event) {
    if (this.isSubmitting) return // Don't show modal if form is submitting
    const target = event.target.closest('a')
    if (!target) return
    
    // Skip if this is an excluded selector
    if (this.isExcludedLink(target)) return
    
    // Skip if target has href="#" or starts with javascript:
    const href = target.getAttribute('href')
    if (!href || href === '#' || href.startsWith('javascript:')) return

    if (this.hasUnsavedChanges()) {
      event.preventDefault()
      this.showUnsavedChangesModal(href)
    }
  }

  showUnsavedChangesModal(targetHref) {
    const modal = document.getElementById('unsavedChangesModal')
    if (!modal) {
      console.warn('Unsaved changes modal not found. Make sure to include the shared modal partial.')
      return
    }

    // Show the modal using Bootstrap
    $(modal).modal('show')

    // Handle the confirm button click
    const confirmButton = modal.querySelector('.btn-confirm-leaving')
    const handleConfirm = () => {
      window.location.href = targetHref
      confirmButton.removeEventListener('click', handleConfirm)
    }
    confirmButton.addEventListener('click', handleConfirm)
  }

  hasUnsavedChanges() {
    // If we have a form controller in the same element, use its dirty state
    const formController = this.application.getControllerForElementAndIdentifier(this.element, 'form')
    if (formController && formController.dirty !== undefined) return formController.dirty

    // Fallback: check if any form fields have been modified from their default values
    return this.formHasChanges()
  }

  formHasChanges() {
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
    if (!form) return false

    // Get all form controls and check if any have been modified
    const formControls = form.querySelectorAll('input, textarea, select')
    const defaultFields = ['authenticity_token'] // Fields to ignore
    
    for (const control of formControls) {
      // Skip certain types and default fields
      if (['submit', 'button', 'reset', 'file'].includes(control.type)) continue
      if (defaultFields.includes(control.name)) continue
      if (control.disabled || control.readOnly) continue

      // For input/textarea, check if value differs from defaultValue
      if ((control.type !== 'checkbox' && control.type !== 'radio')) {
        // For select elements, defaultValue is undefined, so we need to check differently
        if (control.tagName === 'SELECT') {
          // For select elements, compare with the initially selected option
          const initialValue = control.getAttribute('data-initial-value') || control.value
          if (control.value !== initialValue) {
            return true
          }
        } else {
          // For input/textarea, check if value differs from defaultValue
          if (control.value !== control.defaultValue) {
            return true
          }
        }
      }
      
      // For checkboxes and radio buttons, check if checked differs from initial checked state
      if ((control.type === 'checkbox' || control.type === 'radio')) {
        const initialChecked = control.getAttribute('data-initial-checked')
        if (initialChecked !== null) {
          const initialCheckedBool = initialChecked === 'true'
          if (control.checked !== initialCheckedBool) {
            return true
          }
        } else {
          // Fallback to defaultChecked if no initial value stored
          if (control.checked !== control.defaultChecked) {
            return true
          }
        }
      }
    }

    return false
  }

  handleFormSubmit() {
    this.isSubmitting = true

    // Reset the form controller's dirty state when form is submitted
    const formController = this.application.getControllerForElementAndIdentifier(this.element, 'form')
    if (formController && typeof formController.resetDirtyState === 'function') formController.resetDirtyState()
  }

  isOnTargetPath() {
    if (!this.hasPathPatternValue) return true
    
    const pattern = new RegExp(this.pathPatternValue)
    return pattern.test(window.location.pathname)
  }

  storeInitialValues() {
    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
    if (!form) return

    const selectElements = form.querySelectorAll('select')
    selectElements.forEach(select =>  select.setAttribute('data-initial-value', select.value))

    const formElements = form.querySelectorAll('input, textarea')
    formElements.forEach(element => {
      if (element.type === 'checkbox' || element.type === 'radio') {
        element.setAttribute('data-initial-checked', element.checked)
      } else {
        element.setAttribute('data-initial-value', element.value)
      }
    })
  }

  isExcludedLink(link) {
    const excludeSelectors = [
      ...this.defaultExcludeSelectors,
      ...(this.excludeSelectorsValue || [])
    ]

    return excludeSelectors.some(selector => link.matches(selector))
  }
}
